import { registerTmTools } from '../modules/threatModeling/chat';

/**
 * Tool definition for OpenAI function calling
 */
export interface ToolDefinition {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, any>;
      required?: string[];
    };
  };
}

/**
 * Tool executor function type
 */
export type ToolExecutor = (args: any, context: ToolExecutionContext) => Promise<any>;

/**
 * Context passed to tool executors
 */
export interface ToolExecutionContext {
  assessmentId?: number;
  organizationId?: number;
  dispatch?: any; // Redux dispatch function
}

/**
 * Registry entry combining definition and executor
 */
export interface ToolRegistryEntry {
  definition: ToolDefinition;
  executor: ToolExecutor;
}

/**
 * Tool Registry - Maps tool names to their definitions and executors
 */
export class ChatToolRegistry {
  private tools: Map<string, ToolRegistryEntry> = new Map();

  /**
   * Register a new tool
   */
  register(name: string, definition: ToolDefinition, executor: ToolExecutor): void {
    this.tools.set(name, { definition, executor });
  }

  /**
   * Get all tool definitions (for sending to backend)
   */
  getDefinitions(): ToolDefinition[] {
    return Array.from(this.tools.values()).map(entry => entry.definition);
  }

  /**
   * Execute a tool by name
   */
  async execute(name: string, args: any, context: ToolExecutionContext): Promise<any> {
    const tool = this.tools.get(name);
    if (!tool) {
      throw new Error(`Unknown tool: ${name}`);
    }
    return await tool.executor(args, context);
  }

  /**
   * Check if a tool exists
   */
  has(name: string): boolean {
    return this.tools.has(name);
  }
}

/**
 * Create and populate the default tool registry
 */
export function createDefaultToolRegistry(): ChatToolRegistry {
  const registry = new ChatToolRegistry();

  // Register Threat Modeling module tools
  registerTmTools(registry);

  // Future: register other module tools here
  // registerDoraTools(registry);

  return registry;
}

/**
 * Global tool registry instance
 */
export const chatToolRegistry = createDefaultToolRegistry();
